import React from "react";
import { Link } from "react-router-dom";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";

/** Reference page at `/hiw` (HIW). Subscriber step-by-step flow lives at `/HowItWorks`. */

const icMarketsUrl = (process.env.REACT_APP_BROKER_ICMARKETS_URL || "").trim();
const fxProUrl =
  "https://direct-fxpro.com/en/partner/2bDCLQ6wh";

const brokerAdvantages = [
  {
    name: "FxPro",
    points: [
      "Ultra-fast execution designed for active trading conditions.",
      "Stable infrastructure with strong order-routing reliability.",
      "Multiple platform support and flexible account types.",
    ],
  },
  {
    name: "ICMarkets",
    points: [
      "Ultra-fast execution with low-latency trade processing.",
      "Competitive spreads suitable for copy-trading environments.",
      "High-liquidity execution model built for precision fills.",
    ],
  },
];

export default function HowItWorks() {
  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-5xl">
        <LegalPagesBrandHeader />
        <header className="mb-5 space-y-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-300">
            AlphaSniper Trading
          </p>
          <h1 className="text-3xl font-bold sm:text-4xl">How It Works</h1>
          <p className="mx-auto max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            Pick partner onboarding or use your own broker, then complete the
            Quick Sign-Up flow. Both paths use the same auto-copy framework.
          </p>
        </header>

        <div className="space-y-8">
        <section className="grid gap-5 lg:grid-cols-2">
          <article className="rounded-2xl border border-emerald-300/25 bg-emerald-500/[0.08] p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-300">
              Path 1
            </p>
            <h2 className="mt-2 text-2xl font-bold text-white">
              With our broker partners
            </h2>
            <p className="mt-1 text-sm text-emerald-200">
              $25 — 1st month (28.5% off)
            </p>

            <p className="mt-5 text-sm font-semibold text-white sm:text-base">
              Choose one of our partners
            </p>
            <ul className="mt-2 space-y-2 text-sm leading-relaxed text-gray-200 sm:text-base">
              <li className="flex flex-wrap items-center gap-x-2">
                <span className="text-emerald-300">•</span>
                {icMarketsUrl ? (
                  <a
                    href={icMarketsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-brand-300 underline-offset-2 hover:text-brand-200 hover:underline"
                  >
                    ICMarkets
                  </a>
                ) : (
                  <span>ICMarkets (link pending)</span>
                )}
              </li>
              <li className="flex flex-wrap items-center gap-x-2">
                <span className="text-emerald-300">•</span>
                <a
                  href={fxProUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 transition hover:border-brand-300/50 hover:bg-white/[0.08]"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/fxpro-logo.svg`}
                    alt="FxPro"
                    className="h-6 w-auto"
                    loading="lazy"
                  />
                  <span className="font-medium text-brand-300 underline-offset-2 hover:text-brand-200 hover:underline">
                    FxPro — partner link
                  </span>
                </a>
              </li>
            </ul>

            <ol className="mt-6 list-none space-y-3 text-sm leading-relaxed text-gray-200 sm:text-base">
              <li>
                <span className="font-semibold text-white">A.</span> Create a new
                account with our link (use the partner you chose above).
              </li>
              <li>
                <span className="font-semibold text-white">B.</span> Verify your
                account.
              </li>
              <li>
                <span className="font-semibold text-white">C.</span> Deposit at
                least <span className="font-semibold text-white">200 USD</span>{" "}
                with leverage{" "}
                <span className="font-semibold text-white">1:1000</span>{" "}
                <span className="text-emerald-200">(Recommended)</span>.
              </li>
              <li>
                <span className="font-semibold text-white">D.</span> Fill the form
                on the{" "}
                <Link
                  to="/SignUp"
                  className="font-medium text-brand-300 underline-offset-2 hover:text-brand-200 hover:underline"
                >
                  Sign up
                </Link>{" "}
                page.
              </li>
            </ol>
          </article>

          <article className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
              Path 2
            </p>
            <h2 className="mt-2 text-2xl font-bold text-white">
              With your personal broker
            </h2>
            <p className="mt-1 text-sm text-slate-300">
              $30 — 1st month (14.3% off)
            </p>

            <ol className="mt-6 list-none space-y-4 text-sm leading-relaxed text-gray-200 sm:text-base">
              <li>
                <span className="font-semibold text-white">1.</span> Fill the form
                on the{" "}
                <Link
                  to="/SignUp"
                  className="font-medium text-brand-300 underline-offset-2 hover:text-brand-200 hover:underline"
                >
                  Sign up
                </Link>{" "}
                page.
              </li>
              <li>
                <span className="font-semibold text-white">2.</span> After
                completing the form, click{" "}
                <span className="font-semibold text-white">Sign-Up &amp; Pay</span>
                .
              </li>
            </ol>
          </article>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
          <h2 className="text-2xl font-bold text-white">Broker Advantages</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-300 sm:text-base">
            We prioritize brokers known for execution quality, because
            auto-copy performance depends heavily on speed and fill consistency.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {brokerAdvantages.map((broker) => (
              <article
                key={broker.name}
                className="rounded-xl border border-white/10 bg-[#0a102c]/60 p-4"
              >
                <h3 className="text-lg font-semibold text-brand-300">
                  {broker.name}
                </h3>
                <ul className="mt-2 space-y-2 text-sm text-gray-200">
                  {broker.points.map((point) => (
                    <li key={point}>• {point}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-[#0a102c]/60 p-5 text-center">
          <p className="text-sm text-gray-300 sm:text-base">
            Ready to continue? Go to{" "}
            <Link
              to="/SignUp"
              className="font-medium text-brand-300 underline-offset-2 hover:text-brand-200 hover:underline"
            >
              sign up
            </Link>
            .
          </p>
        </section>
        </div>

        <footer className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-gray-400 sm:text-sm">
          <p>AlphaSniper 2026 © All Rights Reserved</p>
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
